import {
  ADD_USER_FLOW_TITLE,
  USER_CREATION_TIMING,
  USER_PROGRESS_RAIL_STEPS,
} from '@/config/addUserFlow';
import { staticDefaults } from '@/config/static/catalog';
import { WORKFLOW_QUERY } from '@/utils/constants';
import type { UserDraft, UserCreationSuccess, UserFormStepId } from '@/types/userCreation';
import { buildUserCreationNavigateUrl as buildUrl, createLocalUserCreationConversationId as createLocalId, isUserCreationConversation as isRegistered, registerUserCreationConversation as register } from '@/utils/conversationWorkflow';
import { registerFromDraft } from '@/services/userRegistry';
import { hasParsedInputPrefill, mergePartialDraft } from '@/utils/draftPrefill';
import { FLOW_CONFIRM_APPROVE_MESSAGE } from '@/utils/flowConfirmCta';
import { buildUserCreationSuccessResult } from '@/utils/userCreationSuccess';
import type { GuidedFlowRuntime } from './types';

const OPERATOR_PARTIAL_PREFILL_HINT = staticDefaults.shared.operatorPartialPrefillHint;

const FORM_STEP_ORDER: UserFormStepId[] = ['user-details', 'credential', 'access-group', 'confirm'];
const LOADING_PHASES = ['thinking', 'working', 'almost-ready'] as const;
const DEFAULT_CREDENTIAL_TYPE: NonNullable<UserDraft['credentialType']> = 'card';

function withDefaultCredentialType(draft: UserDraft): UserDraft {
  if (draft.credentialType) return draft;
  return { ...draft, credentialType: DEFAULT_CREDENTIAL_TYPE };
}

function emptyDraft(): UserDraft {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    title: '',
    employmentNumber: '',
    userGroup: 'None',
    mobileNumber: '',
    address: '',
    credentialType: DEFAULT_CREDENTIAL_TYPE,
    cardNumber: '',
    cardFormat: '26-bit Wiegand (H10301)',
    facilityCode: '',
    pin: '',
    accessGroups: [],
    schedule: '24/7 Always',
    activationDate: new Date().toISOString().split('T')[0],
    expirationDate: '',
  };
}

export const userCreationRuntime: GuidedFlowRuntime<UserDraft, UserFormStepId, UserCreationSuccess> = {
  id: 'user-creation',
  storageKeyPrefix: 'ux3_user_creation_state',
  workflowQuery: WORKFLOW_QUERY.USER_CREATION,
  flowTitle: ADD_USER_FLOW_TITLE,
  progressRailSteps: USER_PROGRESS_RAIL_STEPS,
  timing: USER_CREATION_TIMING,
  loadingPhases: LOADING_PHASES,
  formStepOrder: FORM_STEP_ORDER,
  initialFormStep: 'user-details',
  explainabilityText: staticDefaults.user.explainability,
  formMessageAssistantHint: staticDefaults.user.assistantHint,
  idleHint: "Describe the user you'd like to create, then click the arrow to send.",
  savingTitle: 'Saving your user',
  loadingMessage: 'Preparing user creation…',
  start: {
    conversationTitle: 'New user onboarding',
    defaultStarterMessage: 'Create a new user with a card for this site',
    clearStoredState: () => {
      // user creation uses its own key prefix; hook manages persistence
    },
    registerConversation: register,
    createLocalConversationId: createLocalId,
    buildNavigateUrl: buildUrl,
    isConversationRegistered: isRegistered,
  },
  emptyDraft,
  createIdleState: (conversationId, userRole) => ({
    conversationId,
    flowId: 'user-creation',
    workflowStatus: 'draft',
    uiPhase: 'idle',
    statusLabel: '',
    progressRail: USER_PROGRESS_RAIL_STEPS.map((s) => ({ id: s.id, label: s.label, status: 'pending' as const })),
    formStep: 'user-details',
    draft: emptyDraft(),
    userMessages: [],
    assistantHint: '',
    showExplainability: false,
    explainabilityText: staticDefaults.user.explainability,
    showFormWizard: false,
    showAiBanner: false,
    aiFilledFields: [],
    userRole,
    result: null,
  }),
  hydrateStored: (saved) => {
    if (!LOADING_PHASES.includes(saved.uiPhase as (typeof LOADING_PHASES)[number])) {
      return {
        ...saved,
        draft: withDefaultCredentialType(saved.draft),
        userRole: saved.userRole ?? 'operator',
        aiFilledFields: saved.aiFilledFields ?? [],
        showAiBanner: saved.showAiBanner ?? false,
      };
    }

    if (saved.userMessages.length === 0) {
      return userCreationRuntime.createIdleState(saved.conversationId, saved.userRole ?? 'operator');
    }

    return {
      ...saved,
      uiPhase: 'form',
      formStep: 'user-details',
      statusLabel: ADD_USER_FLOW_TITLE,
      progressRail: saved.progressRail.map((s) => ({ ...s, status: 'completed' as const })),
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      draft: withDefaultCredentialType(
        saved.draft.firstName || hasParsedInputPrefill(saved.aiFilledFields)
          ? saved.draft
          : emptyDraft()
      ),
      userRole: saved.userRole ?? 'operator',
      aiFilledFields: saved.aiFilledFields ?? [],
      showAiBanner: saved.showAiBanner ?? hasParsedInputPrefill(saved.aiFilledFields),
      assistantHint: hasParsedInputPrefill(saved.aiFilledFields)
        ? OPERATOR_PARTIAL_PREFILL_HINT
        : staticDefaults.user.assistantHint,
    };
  },
  buildFormReveal: ({ base, siteLabel, userRole }) => {
    const isOperator = userRole === 'operator';
    const hasParsedPrefill = hasParsedInputPrefill(base.aiFilledFields);
    const defaults =
      hasParsedPrefill || !isOperator
        ? emptyDraft()
        : {
            ...emptyDraft(),
            ...staticDefaults.user.operatorPrefill,
            accessGroups: [...staticDefaults.user.operatorPrefill.accessGroups],
          };
    const formDraft = withDefaultCredentialType(mergePartialDraft(defaults, base.draft));
    const operatorAiFields = [...staticDefaults.user.operatorAiFilledFields];
    const aiFields = hasParsedPrefill
      ? base.aiFilledFields
      : isOperator
        ? operatorAiFields.filter((field) => {
            const key = field as keyof UserDraft;
            const seeded = base.draft[key];
            return !(typeof seeded === 'string' && seeded.trim());
          })
        : [];
    const hint =
      hasParsedPrefill || (isOperator && aiFields.length > 0)
        ? OPERATOR_PARTIAL_PREFILL_HINT
        : staticDefaults.user.assistantHint.replace('First floor', siteLabel);
    return {
      uiPhase: 'form',
      formStep: 'user-details',
      statusLabel: ADD_USER_FLOW_TITLE,
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      showExplainability: false,
      assistantHint: hint,
      introAssistantHint: hint,
      draft: formDraft,
      userRole,
      aiFilledFields: aiFields,
      showAiBanner: aiFields.length > 0,
    };
  },
  patchDraftOnFormMessage: (draft) => draft,
  ensureDraftForFormStep: (draft, step) =>
    step === 'credential' ? withDefaultCredentialType(draft) : draft,
  isConfirmReady: (draft) =>
    Boolean(draft.firstName) &&
    Boolean(draft.lastName) &&
    Boolean(draft.credentialType) &&
    (draft.accessGroups?.length ?? 0) > 0,
  isStepNextDisabled: (stepId, draft) => {
    if (stepId === 'user-details') return !draft.firstName || !draft.lastName;
    if (stepId === 'credential') return !draft.credentialType || !draft.cardNumber;
    if (stepId === 'access-group') return draft.accessGroups.length === 0;
    return false;
  },
  getAiFilledFields: (_draft, userRole) =>
    userRole === 'operator' ? [...staticDefaults.user.operatorAiFilledFields] : [],
  shouldShowExplainability: () => true,
  buildApproveUserMessage: () => FLOW_CONFIRM_APPROVE_MESSAGE,
  buildSuccess: (draft) => {
    const result = buildUserCreationSuccessResult(draft);
    return {
      result,
      assistantHint: `${result.userName} is now in this system. Here's a quick summary and what you can do next.`,
    };
  },
  onApproved: ({ draft, actorEmail }) => {
    if (actorEmail) registerFromDraft(draft, actorEmail);
  },
};

